import styled from '@emotion/styled';
import React from 'react';

import type { LensInputProps } from '../../types';
import { getColorValue, getOffsetFocusRing } from '../../utilities';

const sizes = {
  medium: {
    switchHeight: 16,
    switchWidth: 32,
    knobOffset: 2,
  },
  large: {
    switchHeight: 20,
    switchWidth: 36,
    knobOffset: 2,
  },
};

const colorStyles = {
  knob: {
    active: {
      enabled: getColorValue('white'),
      disabled: getColorValue('disabledContent'),
    },
    inactive: {
      enabled: getColorValue('white'),
      disabled: getColorValue('disabledContent'),
    },
  },
  track: {
    active: {
      enabled: getColorValue('blurple'),
      disabled: getColorValue('disabledBackground'),
    },
    inactive: {
      enabled: getColorValue('grey6'),
      disabled: getColorValue('disabledBackground'),
    },
  },
};

const getKnobTravel = props => {
  return (
    sizes[props.switchSize].switchWidth - sizes[props.switchSize].switchHeight
  );
};

const getKnobSize = props => {
  return (
    sizes[props.switchSize].switchHeight -
    sizes[props.switchSize].knobOffset * 2
  );
};

const SwitchLabel = styled.label`
  display: block;
  position: relative;
`;

const SwitchInput = styled.input<SwitchInputProps>`
  position: absolute;
  opacity: 0;
  cursor: pointer;

  // to overlap SwitchBox and occupy the same space
  z-index: 1;
  margin: 0;
  width: ${props => sizes[props.switchSize].switchWidth}px;
  height: ${props => sizes[props.switchSize].switchHeight}px;

  &:focus-visible ~ .SwitchBox {
    ${getOffsetFocusRing()};
  }
  &:not(:checked) {
    & + .SwitchBox {
      background-color: ${colorStyles.track.inactive.enabled};
    }
    &:disabled + .SwitchBox {
      background-color: ${colorStyles.track.inactive.disabled};
    }
  }
  &:checked {
    & + .SwitchBox {
      background-color: ${colorStyles.track.active.enabled};
    }
    &:disabled + .SwitchBox {
      background-color: ${colorStyles.track.active.disabled};
    }
    & + .SwitchBox:after {
      transform: translateX(${props => getKnobTravel(props)}px);
    }
  }
  &:disabled {
    pointer-events: none;
  }
`;

const SwitchBox = styled.div<SwitchBoxProps>`
  width: ${props => sizes[props.switchSize].switchWidth}px;
  height: ${props => sizes[props.switchSize].switchHeight}px;
  position: relative;
  border-radius: var(--lns-radius-full);
  transition: 0.2s;
  cursor: ${props => (props.isDisabled ? 'default' : 'pointer')};
  &:after {
    content: '';
    position: absolute;
    top: ${props => sizes[props.switchSize].knobOffset}px;
    left: ${props => sizes[props.switchSize].knobOffset}px;
    width: ${props => getKnobSize(props)}px;
    height: ${props => getKnobSize(props)}px;
    border-radius: var(--lns-radius-full);
    transition: 0.15s;
    background-color: ${props =>
      props.isDisabled
        ? colorStyles.knob.active.disabled
        : colorStyles.knob.active.enabled};
  }
`;

const Switch = ({
  isActive,
  isDisabled,
  onChange,
  size = 'medium',
  ariaLabelledby,
  ariaLabel,
  ariaDescribedby,
  ...props
}: SwitchProps &
  Omit<React.ComponentProps<typeof SwitchInput>, 'size'> &
  Omit<
    LensInputProps,
    keyof SwitchProps & Omit<React.ComponentProps<typeof SwitchInput>, 'size'>
  >) => {
  if (ariaLabelledby && ariaLabel) {
    throw new Error(
      'ariaLabelledby and ariaLabel serve the same purpose and therefore cannot be used at the same time. Choose the one that best suites your needs.'
    );
  }

  return (
    <SwitchLabel htmlFor={props.id}>
      <SwitchInput
        {...props}
        checked={isActive}
        disabled={isDisabled}
        onChange={onChange}
        type="checkbox"
        switchSize={size}
        aria-labelledby={ariaLabelledby}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedby}
        aria-checked={isActive}
      />
      <SwitchBox
        className="SwitchBox"
        isDisabled={isDisabled}
        isActive={isActive}
        switchSize={size}
      />
    </SwitchLabel>
  );
};

type SwitchProps = {
  isActive?: boolean;
  isDisabled?: boolean;
  onChange?: React.ReactEventHandler;
  size?: 'medium' | 'large';
  ariaLabelledby?: string;
  ariaLabel?: string;
  ariaDescribedby?: string;
};

type SwitchInputProps = {
  switchSize?: 'medium' | 'large';
};

type SwitchBoxProps = {
  isActive?: boolean;
  isDisabled?: boolean;
  switchSize?: 'medium' | 'large';
};

export default Switch;
